import { Home } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { StatusPill } from "components";
import { BREAK_GLASS_APPROVED_EVENT, BREAK_GLASS_EVENT, MSG_CODE } from "config/constants";
import { useContextController } from "context";
import { IncidentApiError, incidentsApi } from "incidents/api";
import { buildCompliance } from "incidents/compliance";
import {
  displayStatus,
  documentationUnlocked,
  elapsedSinceAlert,
  isAwaitingStaffReview,
  isRecordLocked,
  statusLabel,
  typeLabel
} from "incidents/display";
import { incidentAccess } from "incidents/roles";
import { formatWhen } from "incidents/time";
import {
  FalseAlertReason,
  Incident,
  IncidentDocumentation,
  IncidentNotification,
  ReasonableGroundsDecision,
  YesNoUnsure
} from "incidents/types";
import { validateFinalize } from "incidents/validation";
import PrivateLayout from "layouts/PrivateLayout";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fieldSx, primaryButtonSx, SectionTitle, SourceTag, useActor, usePhrase } from "./common";
import { DocumentationForm } from "./documentationForm";

const REASONS: { value: FalseAlertReason; en: string; fr: string }[] = [
  { value: "no_one_present", en: "No one present", fr: "Personne présente" },
  { value: "normal_activity", en: "Normal activity", fr: "Activité normale" },
  { value: "visitor_or_staff", en: "Visitor or staff", fr: "Visiteur ou membre du personnel" },
  { value: "camera_or_technical", en: "Camera or technical issue", fr: "Problème de caméra ou technique" },
  { value: "other", en: "Other", fr: "Autre" }
];

export default function IncidentDetail({ preview = false }: { preview?: boolean }) {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const previewRole = params.get("as") === "supervisor" ? "supervisor" : preview ? "nurse" : undefined;
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const [{ userData }] = useContextController();
  const actor = useActor(preview ? previewRole : undefined);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<IncidentDocumentation>({});
  const [notes, setNotes] = useState<IncidentNotification[]>([]);
  const [decision, setDecision] = useState<ReasonableGroundsDecision>({ decision: null });
  const [reason, setReason] = useState<FalseAlertReason>("normal_activity");
  const [falseNote, setFalseNote] = useState("");
  const [risk, setRisk] = useState<"yes" | "no">("no");
  const [concern, setConcern] = useState<YesNoUnsure>("no");
  const [amendReason, setAmendReason] = useState("");
  const [amendField, setAmendField] = useState("Location");
  const [amendOld, setAmendOld] = useState("");
  const [amendNew, setAmendNew] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [chain, setChain] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const onApproved = () => setReloadKey((k) => k + 1);
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    incidentsApi
      .get(id)
      .then((record) => {
        if (cancelled) return;
        setIncident(record);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (err instanceof IncidentApiError && err.msgCode === MSG_CODE.BREAK_GLASS_REQUIRED) {
          window.dispatchEvent(new CustomEvent(BREAK_GLASS_EVENT));
        }
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  useEffect(() => {
    if (!incident) return;
    setDraft(incident.documentation ?? {});
    setNotes(incident.notifications ?? []);
    setDecision(incident.reasonable_grounds_decision ?? { decision: null });
  }, [incident]);

  const access = incidentAccess(userData, incident?.facility_id, preview ? previewRole : undefined);
  const base = preview ? "/dev/incidents" : "/incidents";
  const locked = incident ? isRecordLocked(incident) : false;
  const unlocked = incident ? documentationUnlocked(incident) : false;
  const waiting = incident ? isAwaitingStaffReview(incident) : false;
  const compliance = incident ? buildCompliance(incident, new Date()) : null;

  async function run(task: () => Promise<Incident | { incident: Incident; message?: string }>, ok?: string) {
    setBusy(true);
    setMessages([]);
    setError(null);
    try {
      const result = await task();
      const next = "incident" in result ? result.incident : result;
      setIncident(next);
      if ("message" in result && result.message) setMessages([result.message]);
      else if (ok) setMessages([ok]);
    } catch (err) {
      if (err instanceof IncidentApiError && err.errors?.length) setMessages(err.errors);
      else setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  async function saveDraft() {
    if (!incident) return;
    await run(
      () =>
        incidentsApi.saveDocumentation(incident.id, {
          documentation: draft,
          notifications: notes,
          reasonable_grounds_decision: decision,
          actor
        }),
      phrase("Draft saved in NeuraVue. Nothing was sent.", "Brouillon enregistré dans NeuraVue. Rien n'a été envoyé.")
    );
  }

  async function finalize() {
    if (!incident) return;
    const previewRecord: Incident = {
      ...incident,
      documentation: draft,
      notifications: notes,
      reasonable_grounds_decision: decision
    };
    const problems = validateFinalize(previewRecord);
    if (problems.length) {
      setMessages(problems);
      return;
    }
    setBusy(true);
    try {
      const saved = await incidentsApi.saveDocumentation(incident.id, {
        documentation: draft,
        notifications: notes,
        reasonable_grounds_decision: decision,
        actor
      });
      const lockedRecord = await incidentsApi.submit(saved.id, {
        ...actor,
        name: draft.attestation_name || actor.name,
        designation: draft.attestation_designation || actor.designation
      });
      setIncident(lockedRecord);
      setMessages([
        phrase(
          "Record finalized and locked in NeuraVue. It was not sent to a regulator.",
          "Dossier finalisé et verrouillé dans NeuraVue. Il n'a pas été envoyé à un organisme."
        )
      ]);
    } catch (err) {
      if (err instanceof IncidentApiError && err.errors?.length) setMessages(err.errors);
      else setError(err instanceof Error ? err.message : "Could not finalize.");
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <Box>
      <Breadcrumbs aria-label={phrase("Breadcrumb", "Fil d'Ariane")} sx={{ mb: 2 }}>
        <Tooltip title={phrase("Home", "Accueil")}>
          <IconButton component={Link} to="/" size="small" aria-label={phrase("Home", "Accueil")}>
            <Home fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button component={Link} to={base} sx={{ textTransform: "none" }}>
          {phrase("Incident documentation", "Documentation des incidents")}
        </Button>
        <Typography color="text.primary">{id}</Typography>
      </Breadcrumbs>
      {loading ? <Typography>{phrase("Loading record…", "Chargement du dossier…")}</Typography> : null}
      {error ? (
        <Typography role="alert" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      {messages.length ? (
        <Box component="ul" role="status" sx={{ bgcolor: "#F8F1D8", p: 2, borderRadius: 2 }}>
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </Box>
      ) : null}
      {incident && compliance ? (
        <Box display="grid" gap={2} mt={2}>
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700 }}>
              {incident.id}
            </Typography>
            <Typography sx={{ color: COLORS.surface.secondaryText, mt: 0.5 }}>
              {incident.facility_name} · {typeLabel(incident.type, locale)} · {phrase("Room", "Chambre")}{" "}
              {incident.room || "—"}
            </Typography>
            <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
              <StatusPill
                label={statusLabel(displayStatus(incident), locale)}
                tone={waiting ? "info" : locked ? "success" : "warning"}
              />
              {locked ? <StatusPill label={phrase("Locked", "Verrouillé")} tone="neutral" /> : null}
              <Button
                component={Link}
                to={`${base}/${incident.id}/export${preview ? `?as=${previewRole}` : ""}`}
                sx={primaryButtonSx}
              >
                {phrase("Export / print", "Exporter / imprimer")}
              </Button>
            </Box>
          </Box>

          {waiting ? (
            <Box sx={{ p: 2, bgcolor: "#E8F3FB", borderRadius: 2 }} role="note">
              <Typography sx={{ fontWeight: 700 }}>
                {phrase("Awaiting staff review", "En attente de révision du personnel")}
              </Typography>
              <Typography>
                {phrase(
                  "This is the alert record and the timeline only. The incident form starts after you confirm that you opened the alert. Countdowns start at that confirmation, not at the alert time. Nothing is submitted for you.",
                  "Ceci est seulement le dossier d'alerte et la chronologie. Le formulaire commence quand vous confirmez avoir ouvert l'alerte. Les délais partent de cette confirmation, pas de l'heure de l'alerte. Rien n'est envoyé à votre place."
                )}
              </Typography>
            </Box>
          ) : null}

          <section>
            <SectionTitle
              title={phrase("Alert record", "Dossier d'alerte")}
              hint={phrase(
                "These facts come from the alert. They stay as first written.",
                "Ces faits viennent de l'alerte. Ils restent tels qu'écrits."
              )}
            />
            <Box sx={{ p: 2, bgcolor: "#F4F5F7", borderRadius: 2, display: "grid", gap: 1 }}>
              <SourceTag filledBySystem />
              <Fact label={phrase("Alert", "Alerte")} value={incident.alert_id} />
              <Fact label={phrase("Sub-type", "Sous-type")} value={incident.alert_subtype || "—"} />
              <Fact
                label={phrase("Alert time", "Heure de l'alerte")}
                value={formatWhen(incident.alert_at, locale === "fr" ? "fr-CA" : "en-CA")}
              />
              <Fact
                label={phrase("Camera and place", "Caméra et lieu")}
                value={`${incident.camera_id || "—"} · ${incident.location || "—"}`}
              />
              <Fact
                label={phrase("What the system recorded", "Ce que le système a enregistré")}
                value={incident.alert_summary}
              />
              <Fact
                label={phrase("Staff notified", "Personnel avisé")}
                value={
                  incident.notified_staff
                    .map(
                      (person) =>
                        `${person.name} (${person.role}) ${formatWhen(person.at, locale === "fr" ? "fr-CA" : "en-CA")}`
                    )
                    .join("; ") || "—"
                }
              />
              <Fact
                label={phrase("Time since the alert", "Temps depuis l'alerte")}
                value={elapsedSinceAlert(incident, new Date(), locale).replace(/^.*:\s*/, "")}
              />
              <Typography sx={{ fontSize: 13, color: COLORS.surface.secondaryText }}>
                {phrase(
                  "No injury photos are stored here. A clip reference is an identifier only.",
                  "Aucune photo de blessure n'est conservée ici. La référence du clip est seulement un identifiant."
                )}{" "}
                {incident.clip_ref ? incident.clip_ref.id : ""}
              </Typography>
            </Box>
          </section>

          <section>
            <SectionTitle title={phrase("Response timeline", "Chronologie")} />
            <Box component="ol" sx={{ m: 0, pl: 3 }}>
              {incident.events.map((event) => (
                <li key={event.id}>
                  <Typography>
                    {formatWhen(event.at, locale === "fr" ? "fr-CA" : "en-CA")} —{" "}
                    {event.actor_name || event.actor_user_id} — {event.type.replace(/_/g, " ")}
                    {event.payload?.note ? ` — ${event.payload.note}` : ""}
                  </Typography>
                </li>
              ))}
            </Box>
          </section>

          {waiting && access.canDocument ? (
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={busy}
                sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                onClick={() => run(() => incidentsApi.confirm(incident.id, actor))}
              >
                {phrase(
                  "I opened this alert — start documentation",
                  "J'ai ouvert cette alerte — commencer la documentation"
                )}
              </Button>
              <Button
                disabled={busy}
                sx={primaryButtonSx}
                onClick={() =>
                  run(() =>
                    incidentsApi.escalate(
                      incident.id,
                      actor,
                      phrase("Escalated from review.", "Escaladé pendant la révision.")
                    )
                  )
                }
              >
                {phrase("Escalate", "Escalader")}
              </Button>
            </Box>
          ) : null}

          {incident.status === "false_alert_pending_confirm" && incident.false_alert ? (
            <section>
              <SectionTitle
                title={phrase("Dismissal pending confirmation", "Rejet en attente de confirmation")}
                hint={phrase(
                  "Marking an alert false does not remove anyone's duty to report. A supervisor cannot block escalation.",
                  "Marquer une alerte comme fausse n'enlève pas le devoir de signaler. Un superviseur ne peut pas bloquer l'escalade."
                )}
              />
              <Fact label={phrase("Reason", "Raison")} value={incident.false_alert.reason_code.replace(/_/g, " ")} />
              <Fact label={phrase("Note", "Note")} value={incident.false_alert.note || "—"} />
              <Fact
                label={phrase("Marked by", "Marqué par")}
                value={`${incident.false_alert.dismissed_by_name} · ${formatWhen(incident.false_alert.dismissed_at)}`}
              />
              {access.canSupervise ? (
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    disabled={busy || actor.user_id === incident.false_alert.dismissed_by}
                    sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
                    onClick={() =>
                      run(() => incidentsApi.confirmFalseAlert(incident.id, { decision: "confirm", actor }))
                    }
                  >
                    {phrase("Confirm dismissal", "Confirmer le rejet")}
                  </Button>
                  <Button
                    disabled={busy || actor.user_id === incident.false_alert.dismissed_by}
                    sx={primaryButtonSx}
                    onClick={() =>
                      run(() =>
                        incidentsApi.confirmFalseAlert(incident.id, { decision: "reject", note: reviewNote, actor })
                      )
                    }
                  >
                    {phrase("Reject — open the incident", "Rejeter — ouvrir l'incident")}
                  </Button>
                </Box>
              ) : (
                <Typography>
                  {phrase(
                    "A manager or director of care must confirm this dismissal. You can still escalate.",
                    "Une gestionnaire ou la directrice des soins doit confirmer ce rejet. Vous pouvez encore escalader."
                  )}
                </Typography>
              )}
              {access.canDocument ? (
                <Button
                  sx={{ ...primaryButtonSx, mt: 1 }}
                  disabled={busy}
                  onClick={() => run(() => incidentsApi.escalate(incident.id, actor, "Escalated."))}
                >
                  {phrase("Escalate", "Escalader")}
                </Button>
              ) : null}
            </section>
          ) : null}

          {(waiting || (unlocked && !locked)) &&
          access.canDocument &&
          incident.status !== "false_alert_pending_confirm" ? (
            <section>
              <SectionTitle
                title={phrase("False alert", "Fausse alerte")}
                hint={phrase(
                  "Marking an alert false does not remove anyone's duty to report under provincial law.",
                  "Marquer une alerte comme fausse n'enlève pas le devoir de signaler prévu par la loi."
                )}
              />
              <Box display="grid" gap={1.5} maxWidth={640}>
                <TextField
                  select
                  label={phrase("Reason", "Raison")}
                  value={reason}
                  onChange={(event) => setReason(event.target.value as FalseAlertReason)}
                  sx={fieldSx}
                >
                  {REASONS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {locale === "fr" ? item.fr : item.en}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={phrase("Note", "Note")}
                  value={falseNote}
                  onChange={(event) => setFalseNote(event.target.value)}
                  multiline
                  minRows={2}
                  sx={fieldSx}
                />
                <TextField
                  select
                  label={phrase("Any sign of injury or distress?", "Signe de blessure ou de détresse?")}
                  value={risk}
                  onChange={(event) => setRisk(event.target.value as "yes" | "no")}
                  sx={fieldSx}
                >
                  <MenuItem value="no">{phrase("No", "Non")}</MenuItem>
                  <MenuItem value="yes">
                    {phrase("Yes — this stays an incident", "Oui — ceci reste un incident")}
                  </MenuItem>
                </TextField>
                <TextField
                  select
                  label={phrase("Any concern about care or conduct?", "Inquiétude sur les soins ou la conduite?")}
                  value={concern}
                  onChange={(event) => setConcern(event.target.value as YesNoUnsure)}
                  sx={fieldSx}
                >
                  <MenuItem value="no">{phrase("No", "Non")}</MenuItem>
                  <MenuItem value="unsure">{phrase("Unsure — cannot dismiss", "Incertain — ne pas rejeter")}</MenuItem>
                  <MenuItem value="yes">{phrase("Yes — cannot dismiss", "Oui — ne pas rejeter")}</MenuItem>
                </TextField>
                <Button
                  sx={primaryButtonSx}
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      incidentsApi.falseAlert(incident.id, {
                        reason_code: reason,
                        note: falseNote,
                        risk_situation: risk,
                        concern,
                        actor
                      })
                    )
                  }
                >
                  {phrase("Mark false alert", "Marquer comme fausse alerte")}
                </Button>
              </Box>
            </section>
          ) : null}

          {unlocked && compliance.clocks.length ? (
            <section aria-label={phrase("Compliance hints", "Repères de conformité")}>
              <SectionTitle
                title={phrase("Compliance hints", "Repères de conformité")}
                hint={compliance.confirmation_note}
              />
              <Typography sx={{ mb: 1 }}>
                {phrase("Confirmed at", "Confirmé à")}{" "}
                {formatWhen(incident.confirmed_at, locale === "fr" ? "fr-CA" : "en-CA")} ·{" "}
                {elapsedSinceAlert(incident, new Date(), locale)}
              </Typography>
              {compliance.route_hint ? <Typography sx={{ mb: 1 }}>{compliance.route_hint}</Typography> : null}
              {compliance.next_business_morning ? (
                <Typography sx={{ mb: 1 }}>
                  {phrase("Next business morning", "Prochain matin ouvrable")}: {compliance.next_business_morning}
                </Typography>
              ) : null}
              {compliance.province_note ? <Typography>{compliance.province_note}</Typography> : null}
              <Box component="ul" sx={{ pl: 2 }}>
                {compliance.clocks.map((clock) => (
                  <li key={clock.id}>
                    <Typography>
                      <strong>{clock.label}</strong> —{" "}
                      {clock.state === "done"
                        ? phrase("Done", "Fait")
                        : clock.state === "due_now"
                          ? phrase("Due now", "À faire maintenant")
                          : clock.state === "overdue"
                            ? phrase("Overdue", "En retard")
                            : clock.state === "needed"
                              ? phrase("Needed to finalize", "Requis avant de finaliser")
                              : clock.due_at
                                ? `${phrase("Due", "Échéance")} ${formatWhen(clock.due_at, locale === "fr" ? "fr-CA" : "en-CA")}`
                                : clock.state}
                    </Typography>
                    <Typography sx={{ color: COLORS.surface.secondaryText }}>{clock.detail}</Typography>
                  </li>
                ))}
              </Box>
            </section>
          ) : null}

          {unlocked ? (
            <DocumentationForm
              incident={incident}
              draft={draft}
              setDraft={setDraft}
              notes={notes}
              setNotes={setNotes}
              decision={decision}
              setDecision={setDecision}
              locked={locked}
              canDocument={access.canDocument}
              busy={busy}
              actorUserId={actor.user_id}
              onSave={saveDraft}
              onFinalize={finalize}
            />
          ) : null}

          <section>
            <SectionTitle
              title={phrase("Amendments", "Modifications")}
              hint={phrase("The original text stays visible.", "Le texte original reste visible.")}
            />
            {incident.amendments.length ? (
              incident.amendments.map((amendment) => (
                <Box
                  key={amendment.id}
                  sx={{ mb: 1.5, pl: 1.5, borderLeft: `3px solid ${COLORS.surface.primaryText}` }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {amendment.author_name} · {formatWhen(amendment.at, locale === "fr" ? "fr-CA" : "en-CA")}
                  </Typography>
                  <Typography>{amendment.reason}</Typography>
                  {amendment.changes.map((change) => (
                    <Typography key={`${amendment.id}-${change.field}`}>
                      {change.field}: {phrase("Original", "Original")} “{change.old}” · {phrase("Amended", "Modifié")} “
                      {change.new}”
                    </Typography>
                  ))}
                </Box>
              ))
            ) : (
              <Typography>{phrase("No amendments yet.", "Aucune modification.")}</Typography>
            )}
            {locked && access.canDocument ? (
              <Box display="grid" gap={1} maxWidth={640} mt={1}>
                <TextField
                  label={phrase("Reason", "Raison")}
                  value={amendReason}
                  onChange={(event) => setAmendReason(event.target.value)}
                  sx={fieldSx}
                />
                <TextField
                  label={phrase("Field", "Champ")}
                  value={amendField}
                  onChange={(event) => setAmendField(event.target.value)}
                  sx={fieldSx}
                />
                <TextField
                  label={phrase("Original", "Original")}
                  value={amendOld}
                  onChange={(event) => setAmendOld(event.target.value)}
                  sx={fieldSx}
                />
                <TextField
                  label={phrase("New", "Nouveau")}
                  value={amendNew}
                  onChange={(event) => setAmendNew(event.target.value)}
                  sx={fieldSx}
                />
                <Button
                  sx={primaryButtonSx}
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      incidentsApi.amend(incident.id, {
                        reason: amendReason,
                        changes: [{ field: amendField, old: amendOld, new: amendNew }],
                        actor
                      })
                    )
                  }
                >
                  {phrase("Add amendment", "Ajouter une modification")}
                </Button>
              </Box>
            ) : null}
          </section>

          {incident.documentation?.supervisor_review ? (
            <section>
              <SectionTitle title={phrase("Manager review", "Révision de la gestion")} />
              <Typography>
                {incident.documentation.supervisor_review.name}, {incident.documentation.supervisor_review.designation},{" "}
                {formatWhen(incident.documentation.supervisor_review.at)}.{" "}
                {incident.documentation.supervisor_review.note}
              </Typography>
              <Typography sx={{ color: COLORS.surface.secondaryText }}>
                {phrase(
                  "This is a separate entry, not a co-signature.",
                  "Ceci est une entrée distincte, pas une contresignature."
                )}
              </Typography>
            </section>
          ) : null}

          {locked && access.canSupervise && incident.status === "submitted_locked" ? (
            <section>
              <SectionTitle
                title={phrase("Director of care review", "Révision de la directrice des soins")}
                hint={phrase(
                  "Your review is your own entry. It does not co-sign the nurse's attestation.",
                  "Votre révision est votre propre entrée. Elle ne contresigne pas l'attestation de l'infirmière."
                )}
              />
              <TextField
                label={phrase("Review note", "Note de révision")}
                value={reviewNote}
                onChange={(event) => setReviewNote(event.target.value)}
                multiline
                minRows={2}
                sx={fieldSx}
              />
              <Button
                sx={{ ...primaryButtonSx, mt: 1 }}
                disabled={busy || !reviewNote.trim()}
                onClick={() =>
                  run(() =>
                    incidentsApi.review(incident.id, actor, reviewNote, actor.designation || "Director of care")
                  )
                }
              >
                {phrase("Record my review", "Enregistrer ma révision")}
              </Button>
            </section>
          ) : null}

          <Button
            sx={primaryButtonSx}
            onClick={() =>
              incidentsApi
                .verify(incident.id)
                .then((result) =>
                  setChain(
                    result.valid
                      ? phrase("Record chain checks out.", "La chaîne du dossier est intacte.")
                      : phrase("Record chain does not check out.", "La chaîne du dossier ne concorde pas.")
                  )
                )
            }
          >
            {phrase("Check record chain", "Vérifier la chaîne du dossier")}
          </Button>
          {chain ? <Typography role="status">{chain}</Typography> : null}
        </Box>
      ) : null}
    </Box>
  );

  if (preview) return <Box sx={{ p: 2, bgcolor: COLORS.surface.page, minHeight: "100vh" }}>{body}</Box>;
  return <PrivateLayout canAccess={() => true}>{body}</PrivateLayout>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Typography>
      <strong>{label}: </strong>
      {value}
    </Typography>
  );
}
